// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Presents workspace documents with their review and source identities.
 *
 * Contract Review uses DocReview IDs for processing but imported Kuali records
 * must remain recognizable by award number. Reading the canonical document
 * projection keeps filtering and display linked to Vault provenance rather
 * than guessing business identity from filenames.
 */
import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button, Checkbox, Input } from '@/components/ui'
import type { Agent } from '@/subsystems/agents'
import type { DocumentListItemWithPipelines } from '../models'
import type { AgentRunHistory, WorkspaceAgentRuns } from '../hooks/useWorkspaceAgentRuns'
import { agentAcceptsDocuments } from '../lib/agentRunRules'
import { buildWorkspaceDocumentEntries, documentsInEntries } from '../lib/workspaceDocumentGroups'
import { FlatWorkspaceDocumentRows } from './FlatWorkspaceDocumentRows'
import { WorkspaceDocumentRows } from './WorkspaceDocumentRows'
import { BulkRunBar } from './WorkspaceRunControls'

const PAGE_SIZE = 10
export type WorkspaceDocumentsMode = 'flat' | 'source-groups'
interface WorkspaceDocumentsTableProps {
  documents: DocumentListItemWithPipelines[]
  loading: boolean
  agents: Agent[]
  runHistory: WorkspaceAgentRuns
  optimisticRunning: Map<string, Set<string>>
  mode: WorkspaceDocumentsMode
  onRun: (agentIds: string[], documentIds: string[]) => void
  onView: (document: DocumentListItemWithPipelines, run: AgentRunHistory, version: number) => void
  onDownload: (document: DocumentListItemWithPipelines) => void
  onManageVersions: (documentId: string) => void
  onDelete: (document: DocumentListItemWithPipelines) => void
}

function pipelineIsRunning(status: string) {
  const value = status.toLowerCase()
  return value === 'running' || value === 'queued' || value === 'pending'
}
function runningAgents(document: DocumentListItemWithPipelines, agents: Agent[]) {
  // Match on the running job's agentId — pipelineId is shared across agents, so
  // matching by pipeline would light up every agent that owns the same pipeline.
  const agentIds = new Set(agents.map((agent) => agent.agentId))
  return new Set(
    (document.pipelineStatuses ?? [])
      .filter((status) => pipelineIsRunning(status.status) && status.agentId && agentIds.has(status.agentId))
      .map((status) => status.agentId as string),
  )
}
export function WorkspaceDocumentsTable({
  documents,
  loading,
  agents,
  runHistory,
  optimisticRunning,
  mode,
  onRun,
  onView,
  onDownload,
  onManageVersions,
  onDelete,
}: WorkspaceDocumentsTableProps) {
  const sourceGroups = mode === 'source-groups'
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const externalIdLabel = useMemo(() => {
    const referenced = documents.filter((document) => document.externalId)
    if (referenced.length === 0) return null
    const labels = new Set(
      referenced.flatMap((document) => (
        document.externalIdLabel ? [document.externalIdLabel] : []
      )),
    )
    return labels.size === 1 ? [...labels][0] : 'External ID'
  }, [documents])

  const allEntries = useMemo(
    () => buildWorkspaceDocumentEntries(documents, '', sourceGroups),
    [documents, sourceGroups],
  )
  const filteredEntries = useMemo(
    () => buildWorkspaceDocumentEntries(documents, filter, sourceGroups),
    [documents, filter, sourceGroups],
  )
  const hasSourceGroups = allEntries.some((entry) => entry.kind === 'group')
  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pagedEntries = filteredEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const pageDocuments = documentsInEntries(pagedEntries)
  const selectedDocuments = documents.filter(
    (document) => selected.has(document.documentId),
  )
  const allPageSelected = pageDocuments.length > 0
    && pageDocuments.every((document) => selected.has(document.documentId))
  const somePageSelected = pageDocuments.some((document) => selected.has(document.documentId))

  const toggleSelected = (documentId: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(documentId)) next.delete(documentId)
      else next.add(documentId)
      return next
    })
  }

  const togglePage = () => {
    setSelected((current) => {
      const next = new Set(current)
      for (const document of pageDocuments) {
        if (allPageSelected) next.delete(document.documentId)
        else next.add(document.documentId)
      }
      return next
    })
  }

  const toggleDocuments = (groupDocuments: DocumentListItemWithPipelines[]) => {
    setSelected((current) => {
      const next = new Set(current)
      const allSelected = groupDocuments.every((document) => next.has(document.documentId))
      for (const document of groupDocuments) {
        if (allSelected) next.delete(document.documentId)
        else next.add(document.documentId)
      }
      return next
    })
  }

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  const runningAgentIdsFor = (document: DocumentListItemWithPipelines) => new Set([
    ...runningAgents(document, agents),
    ...(optimisticRunning.get(document.documentId) ?? []),
  ])

  const bulkRunning = new Set(
    agents
      .filter((agent) => selectedDocuments.some((document) => (
        runningAgentIdsFor(document).has(agent.agentId)
      )))
      .map((agent) => agent.agentId),
  )
  const bulkDisabled = new Set(
    agents
      .filter((agent) =>
        !agentAcceptsDocuments(agent, selectedDocuments)
        || (agent.pipelineId === 'version-diff' && selectedDocuments.some((document) => document.latestVersion < 2))
        || bulkRunning.has(agent.agentId),
      )
      .map((agent) => agent.agentId),
  )

  return (
    <div className="space-y-3">
      <Input
        placeholder={hasSourceGroups
          ? 'Filter by record, document ID, or name…'
          : externalIdLabel
            ? `Filter by document ID, name, or ${externalIdLabel.toLowerCase()}…`
            : 'Filter by document ID or name…'}
        value={filter}
        onChange={(event) => {
          setFilter(event.target.value)
          setPage(1)
        }}
        className={sourceGroups ? 'h-9 w-full text-xs sm:max-w-sm' : 'max-w-sm'}
      />

      <BulkRunBar
        selectedCount={selectedDocuments.length}
        agents={agents}
        disabledAgentIds={bulkDisabled}
        runningAgentIds={bulkRunning}
        onClear={() => setSelected(new Set())}
        onRun={(agentIds) => onRun(agentIds, selectedDocuments.map((document) => document.documentId))}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filteredEntries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {documents.length === 0 ? 'No documents uploaded.' : 'No matching documents.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table
            className={sourceGroups ? 'w-full min-w-[760px] table-fixed text-sm' : 'w-full min-w-[68rem] text-sm'}
            data-testid="cr-doc-table"
          >
            <thead className="bg-muted/40 text-left">
              {sourceGroups ? (
                <tr>
                  <th className="w-10 px-3 py-2 font-medium">
                    <Checkbox
                      aria-label="Select all"
                      checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                      onCheckedChange={togglePage}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Document</th>
                  {externalIdLabel ? (
                    <th className="px-3 py-2 font-medium">Source</th>
                  ) : null}
                  <th className="hidden w-44 px-3 py-2 font-medium xl:table-cell">Uploaded</th>
                  <th className="hidden w-52 px-3 py-2 font-medium lg:table-cell">Latest Run</th>
                  <th className="w-32 px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              ) : (
                <tr>
                  <th className="w-10 px-3 py-2 font-medium">
                    <Checkbox
                      aria-label="Select all"
                      checked={allPageSelected ? true : somePageSelected ? 'indeterminate' : false}
                      onCheckedChange={togglePage}
                    />
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">Document ID</th>
                  <th className="px-3 py-2 font-medium">Document</th>
                  <th className="px-3 py-2 font-medium">Uploaded By</th>
                  <th className="px-3 py-2 font-medium">Uploaded At</th>
                  <th className="px-3 py-2 font-medium">Latest Run</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {sourceGroups ? (
                <WorkspaceDocumentRows
                  entries={pagedEntries}
                  externalIdLabel={externalIdLabel}
                  selected={selected}
                  expandedGroups={expandedGroups}
                  forceExpandGroups={filter.trim().length > 0}
                  agents={agents}
                  runHistory={runHistory}
                  runningAgentIdsFor={runningAgentIdsFor}
                  onToggleDocument={toggleSelected}
                  onToggleDocuments={toggleDocuments}
                  onToggleGroup={toggleGroup}
                  onRun={onRun}
                  onView={onView}
                  onDownload={onDownload}
                  onManageVersions={onManageVersions}
                  onDelete={onDelete}
                />
              ) : (
                <FlatWorkspaceDocumentRows
                  documents={pageDocuments}
                  selected={selected}
                  agents={agents}
                  runHistory={runHistory}
                  runningAgentIdsFor={runningAgentIdsFor}
                  onToggleDocument={toggleSelected}
                  onRun={onRun}
                  onView={onView}
                  onDownload={onDownload}
                  onManageVersions={onManageVersions}
                  onDelete={onDelete}
                />
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filteredEntries.length > 0 && totalPages > 1 ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredEntries.length)} of {filteredEntries.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" aria-label="Previous page" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            <Button variant="outline" size="icon" aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
